/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
// eslint-disable-next-line max-len
import { UIFieldMapping } from '@workflow/ecp-workflow/dynamic-app/component/building-block/dynamic-personal-info/dynamic-personal-info.const';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { PersonalDynamic } from '@workflow/ecp-workflow/dynamic-app/types/personal-dynamic.type';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { getPageIterationId } from '../utilities.service';
import { StringVariableService } from '@workflow/service/string-variable/string-variable.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { TokenService } from '@cudirect/shared-ui-library';
import { LoanType, PhoneType } from '@shared/enum';
import { EMAIL_REGEX } from '@shared/constant/regex.const';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';
import { AdminLoanProduct, ConsumerLoanGroup } from '@shared/model/lender-config.model';
/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaVisibleFunctionsService {
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);

  private isOLA: boolean;
  private isPrefilled: PrimitiveType;
  private emailAddress: PrimitiveType;
  private phoneType: PrimitiveType;
  private firstTimePrefill: PrimitiveType;

  constructor(
    private readonly adminPortalService: AdminPortalFeaturesService,
    private readonly userService: UserService,
    public readonly l360Service: L360Service,
    private readonly stringVariableService: StringVariableService,
    private readonly tokenService: TokenService
  ) {}

  /**
   *  Show OLA Auto FillOption
   */
  public showBorrowerAutoFillOption = (context: LogicFnContext, isCoborrower: boolean = false, protocol: string = 'ola'): boolean => {
    const iterationID = getPageIterationId(context);
    const coAppRoot = isCoborrower ? 'coBorrowers/#/' : '';
    const autoFillStatus = this.statefulService.wrap(`${protocol}:${coAppRoot}borrowerInfo/autoFillStatus`, undefined, iterationID).value;
    const autoFillOption = this.statefulService.wrap(`${protocol}:${coAppRoot}borrowerInfo/autoFillOption`, undefined, iterationID).value;
    const status = !(autoFillOption && autoFillOption === 'MobileNumber' && autoFillStatus && autoFillStatus === true);
    if (!status) {
      this.statefulService.wrap('mem:primary-application-status').value = true;
    }
    return status;
  };

  /**
   *  Show DOB validation
   */
  public showDOBValidation = (context: LogicFnContext, storageId: string): boolean => {
    const iterationID = context.field.pageIterationId;

    const dobstring = this.statefulService.wrap(storageId, undefined, iterationID).value as string;
    if (dobstring === '' || !dobstring) {
      return false;
    }
    const dobnumber = Date.parse(dobstring);

    if (!dobnumber) {
      return false;
    }

    const dob = new Date(dobstring);

    const dateArray = dobstring.split('/') as any[];

    if (!dateArray || dateArray.length < 3 || dateArray[2].length < 4) {
      return false;
    }

    const currentDate = new Date();

    if (dob > currentDate) {
      return false;
    }

    const currentUTCDate = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const givenUTCDate = Date.UTC(dob.getFullYear(), dob.getMonth(), dob.getDate());

    const leapYearCount = this.leapYearDays(dob, currentDate);

    let days = Math.floor((currentUTCDate - givenUTCDate) / (1000 * 60 * 60 * 24));
    days -= leapYearCount;

    const age = days / Number(365);
    return age < Number(18);
  };

  public dontShowOnFirstPreviousEmployCard = (context: LogicFnContext, rootStorageId: string): boolean => {
    const fieldOrCard = context.field || context.card;
    const isPrevious = this.statefulService.wrap(
      `${rootStorageId}/$/isPreviousEmployment`,
      fieldOrCard.iterationId,
      fieldOrCard.pageIterationId
    ).value;
    return !(fieldOrCard.iterationId === 0) && !isPrevious;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public dontShowOnArrayMinimumCard = (context: LogicFnContext, rootStorageId: string): boolean => {
    const fieldOrCard = context.field || context.card;
    return fieldOrCard.iterationId >= context.card.config.arrayMinimum;
  };

  public isLeapYear = (dd: Date): any => {
    const y = dd.getFullYear();
    return (!(y % 4) && y % 100) || !(y % 400);
  };

  public leapYearDays = (date1: Date, date2: Date): number => {
    const secondMonth = 1;
    let count = 0;

    if (date1.getFullYear() === date2.getFullYear() && this.isLeapYear(date1)) {
      count += (date1.getMonth() == secondMonth && date1.getDay() === 29) || date1.getMonth() > secondMonth ? 1 : 0;
    } else {
      const tmp = new Date(date1.getTime());
      while (tmp.getFullYear() <= date2.getFullYear()) {
        if (this.isLeapYear(tmp)) {
          if (tmp.getFullYear() === date2.getFullYear()) {
            if (tmp.getMonth() >= secondMonth) {
              if (date1.getMonth() === secondMonth) {
                count += date1.getDay() === 29 ? 1 : 0;
              } else {
                count += 1;
              }
            }
          } else {
            count += 1;
          }
        }
        tmp.setFullYear(tmp.getFullYear() + 1);
      }
    }
    return count;
  };

  /**
   * Enable email Field validation
   */
  public showEmailField = (_context: LogicFnContext): boolean => {
    this.statefulService.wrap('ss:sso-flow-incorrectemail').value = false;
    const data = _context.value || _context.field?.data;
    const isRegisteredApp = this.statefulService.wrap(`${data.protocol}:isRegisteredApplication`).value;
    const isPrefilled = this.statefulService.wrap(`${data.protocol}:borrowerInfo/isPrefilled`).value;
    const firstTimePrefill = this.statefulService.wrap('ss:firstTimePrefill').value;
    if (!data || !data.value || !isRegisteredApp) {
      this.statefulService.wrap('ss:sso-flow-incorrectemail').value = true;
      return true;
    }
    const value = data.value;
    if (typeof value === 'string') {
      const isMatch = value.match(EMAIL_REGEX);
      if (isPrefilled && firstTimePrefill) {
        this.statefulService.wrap('ss:sso-flow-incorrectemail').value = !!isMatch;
      }
      return !isMatch;
    }
  };

  /**
   * Enable SSN Field based on SSN Optional Field
   */
  public showSSNField = (_context: LogicFnContext, checkFieldId: string): boolean => {
    const storagePath = `ola:${
      _context.field.pageIterationId >= 0 ? 'coBorrowers/' + _context.field.pageIterationId.toString() + '/' : ''
    }`;
    const optionalSSN = this.stringVariableService.parse('#{admin:olaFeatures/canShowSSNOptional}') as boolean;
    const idType = this.statefulService.wrap(`${storagePath}borrowerInfo/isSSNAvailable`).value;
    if (!optionalSSN) {
      return true;
    }
    if (!this.fieldService.createField(checkFieldId, _context.field.flowId, null, _context.field.pageIterationId).visible) {
      return true;
    }
    if (idType === true && optionalSSN) {
      return true;
    }
    if (idType === false && optionalSSN) {
      this.statefulService.wrap(`${storagePath}borrowerInfo/ssn`).value = null;
      return false;
    }
    return false;
  };

  /**
   * Validate mobile number and update respective flag with result. Flag will be used to enable/disable fields
   */
  public validateMobileNumber = (
    _context: LogicFnContext,
    notValidMobileNumberPath: string,
    prefilenableFlagPath: string,
    showMobileNumberLinkPath: string
  ): boolean => {
    // set ss:isNotValidMobileNumber to false by default - assume the data is valid unless it is evaluated otherwise
    this.statefulService.wrap(notValidMobileNumberPath).value = false;
    this.statefulService.wrap(showMobileNumberLinkPath).value = true;
    const data = _context.value || _context.field?.data;
    this.isOLA = data.protocol === LoanType.OLA.toLowerCase();
    this.isPrefilled = this.isOLA
      ? this.statefulService.wrap(`${data.protocol}:borrowerInfo/isPrefilled`).value
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/isPrefilled').value;
    this.emailAddress = this.isOLA
      ? this.statefulService.wrap(`${data.protocol}:borrowerInfo/emailAddress`).value
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').value;
    this.phoneType = this.isOLA
      ? this.statefulService.wrap(`${data.protocol}:borrowerInfo/phoneType`).value
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/phoneType').value;
    this.firstTimePrefill = this.statefulService.wrap('ss:firstTimePrefill').value;
    if (!data?.value) {
      return this.handleDataWithoutValue(data, showMobileNumberLinkPath, notValidMobileNumberPath);
    }
    let value = data?.value;
    if (typeof value === 'string') {
      value = value.replace(/\D/g, '');
      const isMatch = value.length === 10;
      this.statefulService.wrap(notValidMobileNumberPath).value = !isMatch;
      this.statefulService.wrap(showMobileNumberLinkPath).value = !isMatch;
      this.handleDataWithValue(showMobileNumberLinkPath, notValidMobileNumberPath, isMatch);
      return !isMatch;
    }
  };

  /**
   * This method handles scenarios when there is no value of Mobile Number coming in
   * Like HomeBanking when there's a valid Email & No Cell/Home Phone, first time prefilling data from HomeBanking
   * and also the regular flows
   * @param data - current input (Mobile Number) data
   * @param showMobileNumberLinkPath - showMobileNumberLink storage path
   * @param notValidMobileNumberPath - isNotValidMobileNumber storage path
   * @returns boolean
   */
  private handleDataWithoutValue(data: StatefulValue, showMobileNumberLinkPath: string, notValidMobileNumberPath: string) {
    if (this.isPrefilled && this.firstTimePrefill) {
      // For Prefill scenarios such as Home Banking when there's no Mobile Number/Home Phone value
      if (!data?.value) {
        // When there's a valid Email & No Cell/Home Phone -> disabling the Email Field, hiding the mobile number link and
        // flipping the Mobile Number field to Alternate Phone field
        if (this.emailAddress) {
          return this.disableEmailAndHideMobileNumber(data, showMobileNumberLinkPath, notValidMobileNumberPath);
        } else {
          this.statefulService.wrap(notValidMobileNumberPath).value = true;
          this.statefulService.wrap('ss:firstTimePrefill').value = false;
          return true;
        }
      }
    } else {
      this.statefulService.wrap(notValidMobileNumberPath).value = true;
      return true;
    }
  }

  /**
   * This method is responsible for disabling the Email Address field, setting the Primary Phone Type to Home Phone
   * and hiding the Use Mobile Number link
   * @param data - current input (Mobile Number) data
   * @param showMobileNumberLinkPath - showMobileNumberLink storage path
   * @param notValidMobileNumberPath - isNotValidMobileNumber storage path
   * @returns boolean
   */
  private disableEmailAndHideMobileNumber(data: StatefulValue, showMobileNumberLinkPath: string, notValidMobileNumberPath: string) {
    if (!this.statefulService.wrap('ss:sso-flow-incorrectemail').value) {
      this.statefulService.wrap('ss:sso-flow-incorrectemail').value = true;
    }
    const isPhoneTypeMobile = this.phoneType === PhoneType.Mobile;
    const hasIncorrectEmail = this.statefulService.wrap('ss:sso-flow-incorrectemail').value;
    // In order to show the correct toast message with respect to the Primary Identifier to log in,
    // the Phone Type is being set to 'HomePhone'
    if (this.isOLA) {
      this.statefulService.wrap(`${data.protocol}:borrowerInfo/phoneType`).value = PhoneType.HomePhone;
    } else {
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/phoneType').value = PhoneType.HomePhone;
    }
    this.statefulService.wrap(showMobileNumberLinkPath).value = !hasIncorrectEmail;
    this.statefulService.wrap(notValidMobileNumberPath).value = true;
    this.statefulService.wrap('ss:sso-flow-incorrectemail').value = false;
    this.statefulService.wrap('ss:firstTimePrefill').value = !!isPhoneTypeMobile;
    return true;
  }

  /**
   * This method handles scenaios when there is value of Mobile Number coming in
   * like Scenario where we have a valid Email Address & HomePhone during first time prefilling the data from HomeBanking
   * @param showMobileNumberLinkPath - showMobileNumberLink storage path
   * @param notValidMobileNumberPath - isNotValidMobileNumber storage path
   * @param isMatch - boolean value to determine if the input number is valid or not
   */
  private handleDataWithValue(showMobileNumberLinkPath: string, notValidMobileNumberPath: string, isMatch: boolean) {
    if (this.isPrefilled && this.firstTimePrefill) {
      // Scenario where we have a valid Email Address & HomePhone, will be disabling the Email Address field but not the Home Phone
      if (isMatch && this.phoneType.toString() === PhoneType.HomePhone && this.emailAddress) {
        this.statefulService.wrap('ss:sso-flow-incorrectemail').value = false;
        this.statefulService.wrap(notValidMobileNumberPath).value = isMatch;
      }
      this.statefulService.wrap(showMobileNumberLinkPath).value = !isMatch;
      this.statefulService.wrap('ss:firstTimePrefill').value = false;
    }
  }
  /**
   * show Field validation
   */
  public showPersonalField = (context: LogicFnContext, fieldId: string): boolean => {
    const identificationList = this.statefulService.wrap('mem:ola-identificationType-pick-list').valueString;
    let identification_picklist = [];
    if (identificationList) {
      identification_picklist = JSON.parse(identificationList);
    } else {
      identification_picklist = this.l360Service.getPickListTypes('IdentificationType_Fields, IdentificationType');
    }
    if (!identification_picklist || identification_picklist.length == 0) {
      return false;
    }

    const storagePath = `ola:${context.field.pageIterationId >= 0 ? 'coBorrowers/' + context.field.pageIterationId.toString() + '/' : ''}`;
    const idType = this.statefulService.wrap(`${storagePath}borrowerInfo/idType`).valueString;

    let dyanmicPersonalObj = null;

    if (context.field.pageIterationId >= 0) {
      dyanmicPersonalObj = this.statefulService.wrap(`ss:ola-dynamic-Personal-${context.field.pageIterationId}`).valueString;
    } else {
      dyanmicPersonalObj = this.statefulService.wrap('ss:ola-dynamic-Personal').valueString;
    }

    const obj = dyanmicPersonalObj ? JSON.parse(dyanmicPersonalObj) : null;
    if (idType && obj) {
      const visible = obj[fieldId] ? obj[fieldId].isRequired : false;
      return visible;
    }

    const idTypes = identification_picklist.filter((t) => t.value.toLowerCase() === 'identificationtype')[0].pickListItems;
    const dynamicPersonalInfo = identification_picklist.filter((t) => t.value.toLowerCase() === 'identificationtype_fields')[0]
      .pickListItems;

    if (!idType || !idTypes || idTypes.length === 0 || !dynamicPersonalInfo || dynamicPersonalInfo.length === 0) {
      return false;
    }

    const id = idTypes.filter((t) => t.value === idType)[0]?.id;
    const selectedfields = dynamicPersonalInfo.filter((t) => Number(t.id) === Number(id))[0]?.value;

    const personal = this.getPersonalIdType(context, selectedfields);

    const result = personal?.[fieldId] ? personal[fieldId].isRequired : false;
    return result;
  };

  private getPersonalIdType(context: LogicFnContext, selectedfields: string) {
    const personal = JSON.parse(JSON.stringify(UIFieldMapping)) as PersonalDynamic;

    const fieldArrays = selectedfields?.split('|') as any[];
    fieldArrays?.shift();

    if (!fieldArrays || fieldArrays.length === 0) {
      return;
    }

    fieldArrays.forEach((element) => {
      const uiFields = element.split(':') as any[];
      if (uiFields[0] !== 'IssuedByType') {
        if (personal[uiFields[0]]) {
          personal[uiFields[0]].isRequired = /true/i.test((uiFields[1] as string).toLowerCase());
        }
      } else if (uiFields[0] === 'IssuedByType' && personal.IssuedBy.isRequired) {
        switch (uiFields[1]) {
          case 'FreeText':
            personal.IssuingInstitution.isRequired = true;
            break;
          case 'Country':
            personal.IssuingCountry.isRequired = true;
            break;
          case 'State':
            personal.IssuingState.isRequired = true;
        }
      }
    });

    if (context.field.pageIterationId >= 0) {
      this.statefulService.wrap(`ss:ola-dynamic-Personal-${context.field.pageIterationId}`).value = JSON.stringify(personal);
    } else {
      this.statefulService.wrap('ss:ola-dynamic-Personal').value = JSON.stringify(personal);
    }

    return personal;
  }

  public coapplicantEnabled = (context: LogicFnContext): boolean => {
    const selectedLoanProduct = this.getSelectedLoanProduct(context);
    return selectedLoanProduct ? selectedLoanProduct?.allowCoApplicant : false;
  };

  public coSignerEnabled = (context: LogicFnContext): boolean => {
    const selectedLoanProduct = this.getSelectedLoanProduct(context);
    return selectedLoanProduct ? selectedLoanProduct?.allowCoSigner : false;
  };

  public showRelationshipDetail = (context: LogicFnContext): boolean => {
    const selectedLoanProduct = this.getSelectedLoanProduct(context);
    return selectedLoanProduct ? selectedLoanProduct?.showCoApplicantRelationship : false;
  };

  private getSelectedLoanProduct(context: LogicFnContext) {
    const pageId = getPageIterationId(context);
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, pageId).value;
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, pageId).value;

    if (this.adminPortalService?.config?.olaConfiguration && this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      let selectedLoanProduct: any;
      if (selectedLoanGroup?.loanProducts) {
        selectedLoanProduct = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
      }

      return selectedLoanProduct;
    }
    return null;
  }

  public showSelectedRange = (context: LogicFnContext): boolean => {
    const loanType = this.statefulService.wrap(
      'ola:olaApplicationInformation/loanProductName',
      undefined,
      context.field.pageIterationId
    ).value;
    const loanGroup = this.statefulService.wrap(
      'ola:olaApplicationInformation/loanGroupName',
      undefined,
      context.field.pageIterationId
    ).value;
    // Check dropdown has term options. If any then display term dropdown field else hide the field.
    const hasTermOptions = context?.field?.enum?.length > 0 || !!context?.field?.data?.value;

    if (this.adminPortalService?.config?.olaConfiguration && this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      let selectedLoanProduct: any;
      if (selectedLoanGroup?.loanProducts) {
        selectedLoanProduct = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
      }
      return (
        selectedLoanProduct?.isSelectedRange &&
        selectedLoanProduct.requestAmountRanges.length > 0 &&
        !selectedLoanProduct.ignoreLoanAmountSettings &&
        hasTermOptions
      );
    }
  };

  /**
   * Decide to show Loan Term Textbox.
   * If requested loan amount is visible AND (If requested loan amount is empty OR if requested loan amount is invalid)
   * @param context Loan Term Text Field
   * @param loanAountFieldId Loan Amount Filed Id String
   * @returns Boolean
   */
  public showInput = (context: LogicFnContext, loanAountFieldId: string): boolean => {
    const loanType = this.statefulService.wrap(
      'ola:olaApplicationInformation/loanProductName',
      undefined,
      context.field.pageIterationId
    ).value;
    const loanGroup = this.statefulService.wrap(
      'ola:olaApplicationInformation/loanGroupName',
      undefined,
      context.field.pageIterationId
    ).value;

    let loanAmountFieldValid = false;
    if (loanAountFieldId) {
      const loanAmountField = this.fieldService.createField(
        loanAountFieldId,
        context.field.flowId,
        undefined,
        context.field.pageIterationId
      );
      loanAmountFieldValid = loanAmountField?.visible && loanAmountField?.valid;
    }

    if (this.adminPortalService?.config?.olaConfiguration && this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups) {
      const consumerLoanGroups = this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      let selectedLoanProduct: any;
      if (selectedLoanGroup?.loanProducts) {
        selectedLoanProduct = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
      }

      return (
        selectedLoanProduct &&
        !selectedLoanProduct.isSelectedRange &&
        selectedLoanProduct.requestAmountRanges.length > 0 &&
        !selectedLoanProduct.ignoreLoanAmountSettings &&
        loanAmountFieldValid
      );
    }
  };

  public showEmployedFields = (context: LogicFnContext, employmentTypePath: string, isPreviousEmploymentPath: string): boolean => {
    const isPreviousEmployment = this.statefulService.wrap(
      isPreviousEmploymentPath,
      context.field.iterationId,
      context.field.pageIterationId
    ).value;
    const employmentType = this.statefulService.wrap(
      employmentTypePath,
      context.field.iterationId,
      context.field.pageIterationId
    ).valueString;
    return !!(employmentType?.toLowerCase() === '1' || isPreviousEmployment);
  };

  /**
   * Is True(false if passed in as condtional) or not first card
   * @param context field context
   * @param fieldId Field to copy from
   * @param target should field be true or false
   * @param condtional should field condition type.
   */
  public isValidOrNotFirstCard = (context: LogicFnContext, fieldId: string, target = true, condtional = 'eq'): boolean => {
    const field = this.fieldService.createField(fieldId, context.field.flowId, context.field.iterationId, context.field.pageIterationId);
    let isValid = false;
    switch (condtional) {
      case 'eq':
        isValid = field.data && field.data.value === target;
        break;
      case 'ne':
        isValid = !field.data || field.data.value !== target;
        break;
      case 'gt':
        isValid = field.data && field.data.value > target;
        break;
      case 'gte':
        isValid = field.data && field.data.value >= target;
        break;
    }
    return isValid || context.field.iterationId > 0;
  };

  /**
   * Check if card is last manually added
   * @param context field context
   * @param sourcePath path to source type
   * @param automatedSource value of source type for automated
   */
  public showOnLastManualCardOLA = (context: LogicFnContext, employmentStoragePath?: string, moreStoragePath?: string): boolean => {
    const pageIterationId = context.field.pageIterationId;
    const employmentSize = this.statefulService.arraySize(employmentStoragePath, pageIterationId);
    const additionalEmploymentSie = this.statefulService.arraySize(moreStoragePath, pageIterationId);

    if (
      additionalEmploymentSie > 0 ||
      (employmentSize > 1 && (context.field.iterationId === 0 || context.field.iterationId !== employmentSize - 1))
    )
      return false;
    return true;
  };

  public showPage = (context: LogicFnContext): boolean => {
    const pageId = context.page.id;
    const loanProduct = this.getLoanProduct();
    if (loanProduct) {
      const excludedPages = loanProduct.excludedPages as any[];
      if (excludedPages && excludedPages.indexOf(pageId) !== -1) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public dontShowPageOnLogin = (context: LogicFnContext): boolean => {
    if (this.userService?.isAuthenticated) {
      return false;
    }
    return true;
  };

  public showCard = (context: LogicFnContext): boolean => {
    const cardId = context.card.id;
    const loanProduct = this.getLoanProduct();
    if (loanProduct) {
      const excludedCards = loanProduct.excludedCards as any[];
      if (excludedCards && excludedCards.indexOf(cardId) !== -1) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  public showHoursWorked = (context: LogicFnContext, storageId: string): boolean => {
    const frequencyType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
    return frequencyType?.toString().toLowerCase() === 'hourly' ? true : false;
  };

  public isShowLoanDetailDropdown = (context: LogicFnContext, propertyName: string): boolean => {
    const loanProduct = this.getLoanProduct();
    let isVisible = true;
    if (!loanProduct || !loanProduct.olaLoanDetail || !loanProduct.olaLoanDetail[propertyName]) {
      return false;
    }
    isVisible = loanProduct.olaLoanDetail[propertyName].isVisible;
    return isVisible;
  };

  private getLoanGroup(): ConsumerLoanGroup | undefined {
    const selectedLoanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    if (
      selectedLoanGroupName &&
      this.adminPortalService?.config &&
      this.adminPortalService?.config?.olaConfiguration &&
      this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups
    ) {
      const loanGroup = this.adminPortalService?.config?.olaConfiguration?.consumerLoanGroups.filter(
        (t) => t.loanGroup.toLowerCase() == selectedLoanGroupName.toLowerCase()
      );
      return loanGroup && loanGroup.length > 0 ? loanGroup[0] : undefined;
    }

    return undefined;
  }

  private getLoanProduct(): AdminLoanProduct | undefined {
    const selectedLoanTypeName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const loanGroup = this.getLoanGroup();
    if (loanGroup?.loanProducts) {
      const loanProduct = loanGroup.loanProducts.filter(
        (t) =>
          !loanGroup.isLoanGroupHasProduct || (selectedLoanTypeName && t.productName.toLowerCase() == selectedLoanTypeName.toLowerCase())
      );
      return loanProduct && loanProduct.length > 0 ? loanProduct[0] : undefined;
    }
    return undefined;
  }

  public showNewAutoLoanDetails = (context: LogicFnContext, transactionType: string[]): boolean => {
    const selectedTransactionType = this.statefulService.wrap('ola:olaApplicationInformation/transactionType').value;
    const selectedTransactionTypeId = this.statefulService.wrap('ola:olaApplicationInformation/transactionTypeId').value;
    const loanProduct = this.getLoanProduct();
    const transType = loanProduct?.olaLoanDetail.transactionType.pickList.find(p => p.id?.toString() === selectedTransactionTypeId?.toString());
    const toggleCard = transactionType.find((x) => x.toLowerCase() === transType?.value?.toLowerCase() ||
                  x.toLowerCase() === selectedTransactionType?.toString()?.toLowerCase());
    if (toggleCard) {
      return true;
    }
    return false;
  };

  public showOtherLoanPurpose = (context: LogicFnContext, LoanPurpose: string[]): boolean => {
    const selectedLoanPurpose = this.statefulService.wrap('ola:olaApplicationInformation/loanPurposeType').value;
    const toggleCard = LoanPurpose.find((x) => x === selectedLoanPurpose);
    if (toggleCard) {
      return true;
    }
    return false;
  };

  public showDefaultContactInfo = (context: LogicFnContext, isCoborrower: boolean = false, protocol: string = 'ola'): void => {
    const iterationID = getPageIterationId(context);
    const coAppRoot = isCoborrower ? 'coBorrowers/#/' : '';
    const contactInfo = this.statefulService.wrap(`${protocol}:${coAppRoot}borrowerInfo/phoneType`, undefined, iterationID).value;
    if (contactInfo === null || contactInfo === '') {
      this.statefulService.wrap(`${protocol}:${coAppRoot}borrowerInfo/phoneType`, undefined, iterationID).value = 'Mobile';
      this.statefulService.wrap(`${protocol}:${coAppRoot}borrowerInfo/primaryPhoneNumber`, undefined, iterationID).value = '';
    }
  };

  /**
   * Check Card has any Visible Fields in it.
   * @param context Card Logic Fn
   * @returns boolean
   */
  public hasVisibleFields = (context: LogicFnContext): boolean => {
    return context?.card?.fields.some((f) => f.visible);
  };

  public showOlaClarification = (context: LogicFnContext, phoneType: string): boolean => {
    const contactInfo = this.statefulService.wrap('ola:borrowerInfo/phoneType').value;
    if (contactInfo !== phoneType) {
      return false;
    }
    const isOidTokenExist = (): boolean => {
      return this.tokenService.getCachedToken() !== null;
    };
    return !isOidTokenExist();
  };
}
